import { useState, useCallback } from 'react';

interface ConfirmModalConfig {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'danger' | 'warning';
}

interface UseConfirmModalReturn {
  isOpen: boolean;
  config: ConfirmModalConfig | null;
  isLoading: boolean;
  showConfirm: (config: ConfirmModalConfig) => Promise<boolean>;
  handleConfirm: () => void;
  handleCancel: () => void;
}

export const useConfirmModal = (): UseConfirmModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ConfirmModalConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const showConfirm = useCallback((modalConfig: ConfirmModalConfig): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfig(modalConfig);
      setIsOpen(true);
      setResolvePromise(() => resolve);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolvePromise) {
      setIsLoading(true);
      // Small delay to show loading state
      setTimeout(() => {
        resolvePromise(true);
        setIsOpen(false);
        setConfig(null);
        setResolvePromise(null);
        setIsLoading(false);
      }, 100);
    }
  }, [resolvePromise]);

  const handleCancel = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(false);
      setIsOpen(false);
      setConfig(null);
      setResolvePromise(null);
      setIsLoading(false);
    }
  }, [resolvePromise]);

  return {
    isOpen,
    config,
    isLoading,
    showConfirm,
    handleConfirm,
    handleCancel,
  };
};